<template>
  <div class="custom-input">
    <v-tooltip bottom :disabled="!enableTooltip">
      <template v-slot:activator="{ on, attrs }">
        <v-text-field
          outlined
          color="primary accent-4"
          :label="$t(label)"
          :value="fieldValue"
          :dense="dense"
          :autofocus="autofocus"
          :disabled="disabled"
          :readonly="isReadOnly"
          :hide-details="hideDetails"
          :placeholder="$t(placeholder)"
          :type="inputType"
          :rules="$validate.DataValid(rules[type]) ? rules[type] : rules.text"
          @keydown.enter="isSearch ? clickSearch() : null"
          @keypress="formatInput($event)"
          @paste="handlePaste($event)"
          @input="updateValue($event)"
          @blur="onBlur"
          :prefix="$t(prefix)"
          :suffix="$t(suffix)"
          :class="inputClass"
          autocomplete="new-password"
          @click:clear="onClear()"
          :clearable="enabledClear"
          :persistent-placeholder="persistentPlaceholder"
          :ref="inputRef"
          v-bind="attrs"
          v-on="on"
        >
          <template v-slot:append>
            <v-icon v-if="isSearch" @click.prevent="clickSearch">{{ icon.mdiMagnify }}</v-icon>
            <template v-if="type === 'password' && showPwView">
              <v-btn
                v-if="inputType === 'text'"
                icon
                text
                solo
                dense
                v-ripple="false"
                @click.prevent="inputType = 'password'"
              >
                <v-icon size="20">{{ icon.mdiEye }}</v-icon>
              </v-btn>
              <v-btn
                v-else-if="inputType === 'password'"
                icon
                text
                solo
                dense
                v-ripple="false"
                @click.prevent="inputType = 'text'"
              >
                <v-icon size="20">{{ icon.mdiEyeOff }}</v-icon>
              </v-btn>
            </template>
            <slot v-else name="append"></slot>
          </template>
          <template v-slot:append-outer>
            <slot name="append-outer"></slot>
          </template>
          <template v-slot:prepend-inner>
            <slot name="prepend-inner"></slot>
          </template>
        </v-text-field> 
      </template>
      <span>{{ $t(tooltip) }}</span>
    </v-tooltip>
  </div>
</template>

<script>
import { mdiMagnify, mdiEyeOff, mdiEye } from '@mdi/js'

export default {
  name: 'FormInput',
  props: {
    label: {
      type: String,
      required: false,
      default: '',
    },
    fieldValue: {
      type: String | Number,
      required: true,
      default: '',
    },
    type: {
      type: String,
      required: false,
      default: 'text',
    },
    autofocus: {
      type: Boolean,
      required: false,
      default: false,
    },
    hideDetails: {
      type: Boolean,
      required: false,
      default: false,
    },
    required: {
      type: Boolean,
      required: false,
      default: false,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
    placeholder: {
      type: String,
      required: false,
      default: '',
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    customRules: {
      type: Array,
      required: false,
      default: () => [],
    },
    isIntegerOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    isEngNameOnly: {
      type: Boolean,
      required: false,
      default: false,
    },
    isSearch: {
      type: Boolean,
      required: false,
      default: false,
    },
    isSearchId: {
      type: Boolean,
      required: false,
      default: false,
    },
    dense: {
      type: Boolean,
      required: false,
      default: false,
    },
    prefix: {
      type: String,
      required: false,
      default: '',
    },
    suffix: {
      type: String,
      required: false,
      default: '',
    },
    customInputClass: {
      type: String,
      required: false,
      default: '',
    },
    showPwView: {
      type: Boolean,
      required: false,
      default: false,
    },
    dispatchUpdateOnChange: {
      type: Boolean,
      required: false,
      default: false,
    },
    enabledClear: {
      type: Boolean,
      required: false,
      default: false,
    },
    persistentPlaceholder: {
      type: Boolean,
      required: false,
      default: true,
    },
    inputRef: {
      type: String,
      required: false,
      default: 'input',
    },
    enableTooltip: {
      type: Boolean,
      required: false,
      default: false
    },
    tooltipText: {
      type: String,
      required: false,
      default: '',
    }
  },
  computed: {
    rules() {
      return {
        text: [
          v => this.required ? this.$validate.required(v) || this.$t('form.required') : true,
          v => this.isEngNameOnly ? this.$validate.isEngName(v, !this.required) : true,
          ...this.customRules,
        ],
        number: [
          v => this.required ? this.$validate.required(v) || this.$t('form.required') : true,
          v => this.isIntegerOnly ? this.$validate.isNumber(v, !this.required) : true,
          v => this.$validate.isFloating(v, !this.required),
          ...this.customRules,
        ],
        tel: [
          v => this.required ? this.$validate.required(v) || this.$t('form.required') : true,
          v => this.$validate.isPhoneNumber(v, !this.required),
          ...this.customRules,
        ],
        email: [
          v => this.required ? this.$validate.required(v) || this.$t('form.required') : true,
          v => this.$validate.isEmail(v, !this.required),
          ...this.customRules,
        ],
        username: [
          v => this.required ? this.$validate.required(v) || this.$t('form.required') : true,
          v => this.$validate.isUsername(v, !this.required),
          ...this.customRules,
        ],
      }
    },
    isReadOnly() {
      return this.readonly || this.$store.getters.isLoading
    },
    inputClass() {
      let classObj = {
        'pointer-none': this.$store.getters.isLoading,
        'pw-view': this.type === 'password' && this.showPwView,
      }
      if (this.$validate.DataValid(this.customInputClass)) {
        const arr = this.customInputClass.split(' ')
        arr.forEach(name => {
          if (this.$validate.DataValid(name.trim())) {
            classObj[name.trim()] = true
          }
        })
      }

      return classObj
    },
    tooltip() {
      if (this.enableTooltip) {
        if (this.tooltipText) {
          return this.tooltipText;
        } else if (this.placeholder) {
          return this.placeholder;
        } else if (this.label) {
          return this.label;
        }
      }

      return '';
    }
  },
  data: () => ({
    icon: {
      mdiMagnify,
      mdiEyeOff,
      mdiEye,
    },
    searchText: '',
    inputType: 'text',
  }),
  watch: {
    type: {
      handler(val) {
        if (val === 'password') {
          this.inputType = 'password'
        }
      },
      immediate: true,
    },
  },
  methods: {
    formatInput(event) {
      this.$emit('onkeypress', event)
      if (this.isIntegerOnly) {
        return this.$Formatter.number(event)
      } else if (this.isSearchId) {
        return this.$Formatter.id(event)
      } else if (this.isEngNameOnly) {
        return this.$Formatter.engName(event)
      } else if (this.type === 'tel') {
        return this.$Formatter.phone(event)
      } else if (this.type === 'number') {
        return this.$Formatter.floating(event)
      } else if (this.type === 'username') {
        return this.$Formatter.username(event)
      }
    },
    handlePaste(event) {
      event.preventDefault()
      let value = event.clipboardData.getData('text')

      if (this.type === 'tel') {
        value = value.replace(/[^0-9]/gi, '')
      }

      this.$emit('update:fieldValue', value)
      this.$emit('input-value', value)
    },
    updateValue(value) {
      if (value === null) {
        this.searchText = ''
      } else {
        this.searchText = value
      }

      this.$emit('update:fieldValue', this.searchText)
      this.$emit('input-value', this.searchText)
      if (this.dispatchUpdateOnChange === true) {
        this.$store.dispatch('setDataIsUpdated', true)
      }
    },
    onClear() {
      this.searchText = ''
      this.$emit('update:fieldValue', '')
      this.$emit('clear-value', '')
    },
    onBlur(ev) {
      this.$emit('onblur', ev)
    },
    clickSearch() {
      this.$emit('search-click', this.searchText)
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .pw-view {
  & .v-input__append-inner {
    margin: 8px 0 !important;
  }

  &.v-input--dense .v-input__append-inner {
    margin: 0 !important;
  }
}
</style>
